<template>
    <tr v-if="hasAnyAvailability(availabilityType, dates, allotmentType, subAllotment)" :class="contentClass">
        <td @click="isAllotmentExpanded = !isAllotmentExpanded">
            <span v-if="allotmentType !== 'CUSTOMER'">
                {{ $t(`allotmentType.${allotmentType}`) }}
            </span>
            <span v-else> {{ subAllotment.name }} </span>
            <v-icon :icon="`mdi-chevron-${isAllotmentExpanded ? 'up' : 'down'}`" />
        </td>
        <td v-for="day in dates" :key="`${day}`" class="text-center" :class="dateCellCSSClass(day)">
            <AvailabilityTableLabel
                :availability="gatAvailability(availabilityType, day, allotmentType, null, null, subAllotment)"
            />
        </td>
    </tr>
    <template v-if="isAllotmentExpanded">
        <template v-if="basicRoomGroups.length > 0">
        <template
            v-for="(basicRoomGroup, basicRoomIndex) in basicRoomGroups"
            :key="basicRoomGroup.basicRoomId || 'no-basic-room'"
        >
            <!-- Basic Room Row -->
            <tr :class="contentClass">
                <td class="ps-5" @click="isBasicRoomExpanded[basicRoomIndex] = !isBasicRoomExpanded[basicRoomIndex]">
                    <div class="text-no-wrap">
                        {{ basicRoomGroup.basicRoomName }}
                        <v-icon :icon="`mdi-chevron-${isBasicRoomExpanded[basicRoomIndex] ? 'up' : 'down'}`" />
                    </div>
                    <div
                        v-if="basicRoomGroup.basicRoom?.serviceId"
                        class="text-no-wrap text-caption text-grey-darken-1"
                    >
                        [ID: {{ basicRoomGroup.basicRoom.serviceId }}]
                    </div>
                </td>
                <td
                    v-for="day in dates"
                    :key="`${basicRoomGroup.basicRoomId}-${day}`"
                    :class="dateCellCSSClass(day)"
                    class="text-center"
                >
                    <ZSHAvailabilityTableLabel
                        :availability="
                            getBasicRoomGroupAvailability(
                                availabilityType,
                                day,
                                allotmentType,
                                basicRoomGroup,
                                subAllotment
                            )
                        "
                    />
                </td>
            </tr>
            <template v-if="isBasicRoomExpanded[basicRoomIndex]">
                <!-- Sustainable Room Row -->
                <tr
                    v-if="basicRoomGroup.sustainableRoom"
                    :key="`sustainable-${basicRoomGroup.basicRoomId}`"
                    :class="contentClass"
                >
                    <td class="ps-10">
                        <div class="text-no-wrap">{{ basicRoomGroup.sustainableRoom.serviceName }}</div>
                        <div class="text-no-wrap text-caption text-grey">(SUSTAINABLE)</div>
                        <div
                            v-if="basicRoomGroup.sustainableRoom?.serviceId"
                            class="text-no-wrap text-caption text-grey-darken-1"
                        >
                            [ID: {{ basicRoomGroup.sustainableRoom.serviceId }}]
                        </div>
                    </td>
                    <td
                        v-for="day in dates"
                        :key="`sustainable-${basicRoomGroup.basicRoomId}-${day}`"
                        :class="dateCellCSSClass(day)"
                        class="text-center"
                    >
                        <ZSHAvailabilityTableLabel
                            :availability="
                                getZSHAvailability(
                                    availabilityType,
                                    day,
                                    allotmentType,
                                    basicRoomGroup,
                                    'sustainable',
                                    subAllotment
                                )
                            "
                        />
                    </td>
                </tr>
                <!-- Zero Room Row -->
                <tr v-if="basicRoomGroup.zeroRoom" :key="`zero-${basicRoomGroup.basicRoomId}`" :class="contentClass">
                    <td class="ps-10">
                        <div class="text-no-wrap">{{ basicRoomGroup.zeroRoom.serviceName }}</div>
                        <div class="text-no-wrap text-caption text-grey">(ZERO)</div>
                        <div
                            v-if="basicRoomGroup.zeroRoom?.serviceId"
                            class="text-no-wrap text-caption text-grey-darken-1"
                        >
                            [ID: {{ basicRoomGroup.zeroRoom.serviceId }}]
                        </div>
                    </td>
                    <td
                        v-for="day in dates"
                        :key="`zero-${basicRoomGroup.basicRoomId}-${day}`"
                        :class="dateCellCSSClass(day)"
                        class="text-center"
                    >
                        <ZSHAvailabilityTableLabel
                            :availability="
                                getZSHAvailability(
                                    availabilityType,
                                    day,
                                    allotmentType,
                                    basicRoomGroup,
                                    'zero',
                                    subAllotment
                                )
                            "
                        />
                    </td>
                </tr>
            </template>
        </template>
        </template>
        <!-- Fallback to core category/serviceName grouping when room type data is unavailable -->
        <template v-else>
            <template
                v-for="(categoryName, categoryIndex) in getCategoryNames(availabilityType, allotmentType)"
                :key="categoryName"
            >
                <tr :class="contentClass">
                    <td class="ps-5" @click="isCategoryExpanded[categoryIndex] = !isCategoryExpanded[categoryIndex]">
                        {{ categoryName }}
                        <v-icon :icon="`mdi-chevron-${isCategoryExpanded[categoryIndex] ? 'up' : 'down'}`" />
                    </td>
                    <td
                        v-for="day in dates"
                        :key="`${categoryName}-${day}`"
                        :class="dateCellCSSClass(day)"
                        class="text-center"
                    >
                        <ZSHAvailabilityTableLabel
                            :availability="
                                gatAvailability(availabilityType, day, allotmentType, categoryName, null, subAllotment)
                            "
                        />
                    </td>
                </tr>
                <template v-if="isCategoryExpanded[categoryIndex]">
                    <tr
                        v-for="serviceName in getServiceNames(availabilityType, allotmentType, categoryName)"
                        :key="`${categoryName}-${serviceName}`"
                        :class="contentClass"
                    >
                        <td class="ps-10">
                            {{ serviceName }}
                        </td>
                        <td
                            v-for="day in dates"
                            :key="`${categoryName}-${serviceName}-${day}`"
                            :class="dateCellCSSClass(day)"
                            class="text-center"
                        >
                            <ZSHAvailabilityTableLabel
                                :availability="
                                    gatAvailability(
                                        availabilityType,
                                        day,
                                        allotmentType,
                                        categoryName,
                                        serviceName,
                                        subAllotment
                                    )
                                "
                            />
                        </td>
                    </tr>
                </template>
            </template>
        </template>
    </template>
</template>

<script setup lang="ts">
    import {gatAvailability} from '~src/utils/availability'
    import {getZSHAvailability, getBasicRoomGroupAvailability, type RoomGroup} from '../../utils/zshAvailability'
    import ZSHAvailabilityTableLabel from './ZSHAvailabilityTableLabel.vue'
    import {useSupplierStore} from '~src/stores/SupplierStore'

    const {allotmentType, availabilityType, contentClass, dates, contract} = defineProps<{
        allotmentType: 'NETTO' | 'FREE_SALE' | 'CUSTOMER'
        contentClass?: string
        availabilityType: any[]
        dates: Date[]
        subAllotment?: any
        contract?: any
    }>()

    const {$api} = useNuxtApp()
    const supplierStore = useSupplierStore()
    const roomTypes = ref([])
    const roomTypesLoaded = ref(false)
    const hotelServices = ref([])
    const hotelServicesLoaded = ref(false)

    const isAllotmentExpanded = ref(false)
    const isBasicRoomExpanded = ref([])
    const isCategoryExpanded = ref([])

    // Computed property for basic room groups that updates when room types and hotel services change
    const basicRoomGroups = computed(() => {
        if (!roomTypesLoaded.value || !hotelServicesLoaded.value) return []

        return getBasicRoomGroups(availabilityType, allotmentType)
    })

    // Track category expand state for fallback template
    watch(
        () => getCategoryNames(availabilityType, allotmentType).length,
        (newLen, oldLen) => {
            if (newLen !== oldLen) {
                isCategoryExpanded.value = newLen ? Array(newLen).fill(false) : []
            }
        },
        {immediate: true}
    )

    // Only reset expand state when group count changes, not on every recompute
    watch(
        () => basicRoomGroups.value.length,
        (newLen, oldLen) => {
            if (newLen !== oldLen) {
                isBasicRoomExpanded.value = newLen ? Array(newLen).fill(false) : []
            }
        },
        {immediate: true}
    )

    // Load room types and hotel services when component mounts
    onMounted(async () => {
        await loadHotelServices()
        await loadRoomTypes()
    })

    // Retry room type loading once hotelInfo becomes available (race condition fix)
    watch(
        () => supplierStore.hotelInfo?.hotelId,
        async hotelId => {
            if (hotelId && roomTypesLoaded.value && roomTypes.value.length === 0) {
                roomTypesLoaded.value = false
                await loadRoomTypes()
            }
        }
    )

    async function loadRoomTypes() {
        if (roomTypesLoaded.value) return

        try {
            // Get hotel ID from supplier store
            const hotelId = supplierStore.hotelInfo?.hotelId
            if (!hotelId) {
                roomTypesLoaded.value = true
                return
            }

            const response = await $api.hotelDescriptionRoomType.get(hotelId, {locale: 'all'})
            roomTypes.value = response.hotelRoomTypes || []
            roomTypesLoaded.value = true
        } catch (error) {
            console.error('Error loading room types:', error)
            roomTypes.value = []
            roomTypesLoaded.value = true
        }
    }

    async function loadHotelServices() {
        if (hotelServicesLoaded.value) return

        try {
            // Load hotel services to get serviceId to room type mapping
            const response = await $api.ownHotelServices.get({contractId: contract?.id})
            hotelServices.value = response.services || []
            hotelServicesLoaded.value = true
        } catch (error) {
            console.error('Error loading hotel services:', error)
            hotelServices.value = []
            hotelServicesLoaded.value = true
        }
    }

    function getRoomTypeByServiceId(serviceId: any) {
        // First try to find the service in hotel services to get the room type ID
        const hotelService = hotelServices.value.find(service => service.id === serviceId)
        if (hotelService && hotelService.hotelRoomTypeId) {
            // Use the hotelRoomTypeId from hotel service to find the room type
            const roomType = roomTypes.value.find(rt => rt.id === hotelService.hotelRoomTypeId)
            if (roomType) {
                return roomType
            }
        }

        // Fallback: try direct serviceId match with room type ID
        const directMatch = roomTypes.value.find(rt => rt.id === serviceId)
        if (directMatch) {
            return directMatch
        }

        return null
    }

    function getBasicRoomGroups(availabilityType: any, allotmentType: any): RoomGroup[] {
        // Get all availability items for this allotment type
        const availabilityItems = availabilityType.filter(
            (availability: any) => availability.allotmentType === allotmentType
        )

        // Group by basic room ID using only room type data with sustainabilityLevel
        const groupsMap = new Map<string | number, RoomGroup>()

        availabilityItems.forEach((item: any) => {
            // Find room type by serviceId only - this ensures proper mapping for same-name room types
            const roomType = getRoomTypeByServiceId(item.serviceId)

            if (!roomType) {
                // Skip items without matching room type data to avoid incorrect grouping
                return
            }

            // ZSH-873: hide disabled (inactive) room types from the Availability tab
            if (roomType.active === false) {
                return
            }

            const sustainabilityLevel = roomType.sustainabilityLevel || 'BASIC'
            let basicRoomId: any, basicRoomName: any

            if (sustainabilityLevel === 'BASIC') {
                basicRoomId = roomType.id
                basicRoomName = roomType.name?.en || roomType.name || item.serviceName
            } else {
                // For sustainable/zero rooms, use the basicRoomId to group them
                basicRoomId = roomType.basicRoomId
                if (!basicRoomId) {
                    // Skip if no basicRoomId is defined
                    return
                }
                // Find the basic room name
                const basicRoom = roomTypes.value.find(rt => rt.id === basicRoomId)
                basicRoomName = basicRoom?.name?.en || basicRoom?.name || 'Unknown Basic Room'
            }

            if (!groupsMap.has(basicRoomId)) {
                groupsMap.set(basicRoomId, {
                    basicRoomId,
                    basicRoomName,
                    basicRoom: null,
                    sustainableRoom: null,
                    zeroRoom: null,
                })
            }

            const group = groupsMap.get(basicRoomId)!

            // Assign to appropriate room type based on sustainability level
            if (sustainabilityLevel === 'SUSTAINABLE') {
                group.sustainableRoom = item
            } else if (sustainabilityLevel === 'ZERO') {
                group.zeroRoom = item
            } else {
                group.basicRoom = item
            }
        })

        const result = Array.from(groupsMap.values()).filter(
            group => group.basicRoom || group.sustainableRoom || group.zeroRoom
        )

        return result
    }

    function hasAnyAvailability(availabilityType: any, dates: any, allotmentType: any, subAllotment = null) {
        return !!dates.find((date: any) =>
            gatAvailability(availabilityType, date, allotmentType, null, null, subAllotment) !== null
        )
    }

    function dateCellCSSClass(day: any) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const dayDate = new Date(day)
        dayDate.setHours(0, 0, 0, 0)

        if (dayDate.getTime() === today.getTime()) {
            return 'bg-blue-lighten-5'
        }
        return ''
    }

    // Fallback: core category/serviceName grouping when room type data is unavailable
    function getCategoryNames(availabilityType: any, allotmentType: any) {
        return Array.from(
            new Set(
                availabilityType
                    .filter((a: any) => a.allotmentType === allotmentType)
                    .map(({categoryName}: any) => categoryName)
            )
        )
    }

    function getServiceNames(availabilityType: any, allotmentType: any, categoryName: any) {
        return Array.from(
            new Set(
                availabilityType
                    .filter((a: any) => a.allotmentType === allotmentType && a.categoryName === categoryName)
                    .map(({serviceName}: any) => serviceName)
            )
        )
    }
</script>
